import React, { useState, useEffect, useCallback } from 'react';
import QuickPost from './QuickPost';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { geographicAPI } from '../services/apiEndpoints';
import { DATAFORSEO_LOCATIONS } from '../data/locations';
import {
    MagnifyingGlassIcon,
    GlobeIcon,
    CheckCircledIcon,
    RocketIcon,
    TargetIcon,
    PlusIcon,
    ReloadIcon,
    UpdateIcon,
} from '@radix-ui/react-icons';

function GeographicOptimizer({ user }) {
    const [keyword, setKeyword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [geoData, setGeoData] = useState(null);
    const [recentSearches, setRecentSearches] = useState([]);
    const [locationCode] = useState('2840');
    const [showQuickPost, setShowQuickPost] = useState(false);
    const [prefilledTopic, setPrefilledTopic] = useState('');
    const [prefilledLocation, setPrefilledLocation] = useState('');

    useEffect(() => {
        geographicAPI.getRecent()
            .then(res => {
                if (res.data?.success) setRecentSearches(res.data.data || []);
            })
            .catch(() => {});
    }, []);

    const handleAnalyze = useCallback(async (kw = keyword, force = false) => {
        const trimmed = (kw || '').trim();
        if (!trimmed) {
            setError('Please enter a keyword');
            return;
        }

        setLoading(true);
        setError(null);
        setGeoData(null);

        try {
            const res = await geographicAPI.analyze(trimmed, locationCode, force);
            const data = res.data;

            if (!data.success) {
                setError(data.message || 'Analysis failed.');
                return;
            }

            setGeoData(data);
            setKeyword(trimmed);

            // Refresh recent pill bar
            geographicAPI.getRecent()
                .then(r => { if (r.data?.success) setRecentSearches(r.data.data || []); })
                .catch(() => {});

        } catch (err) {
            const msg = err.response?.data?.message || 'Analysis failed. Please try again.';
            setError(msg);
        } finally {
            setLoading(false);
        }
    }, [keyword, locationCode]);

    const handleRecentClick = (item) => {
        setKeyword(item.keyword);
        handleAnalyze(item.keyword, false);
    };

    const handleRefresh = () => handleAnalyze(geoData?.keyword || keyword, true);

    const getPrefilledLocation = (isoCode) => {
        const entry = DATAFORSEO_LOCATIONS[isoCode];
        // Return the DataForSEO numeric code as string, or "0" for Global
        return entry && entry.code ? String(entry.code) : '0';
    };

    const handleCreateVersion = (country) => {
        setPrefilledTopic(`${geoData.keyword} in ${country.name}`);
        setPrefilledLocation(getPrefilledLocation(country.code));
        setShowQuickPost(true);
    };

    const handleCreateBoth = () => {
        if (localizationCandidates.length > 0) {
            const country = localizationCandidates[0];
            setPrefilledTopic(`${geoData.keyword} in ${country.name}`);
            setPrefilledLocation(getPrefilledLocation(country.code));
            setShowQuickPost(true);
        }
    };

    const localizationCandidates = geoData?.countries?.slice(0, 2) ?? [];

    const InterestBar = ({ country }) => (
        <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                    <span className="text-2xl">{country.flag}</span>
                    <span className="text-zinc-300 font-medium">{country.name}</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-zinc-500">{country.interest}</span>
                    {country.interest === 100 && (
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">Highest</Badge>
                    )}
                    {country.interest < 50 && (
                        <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/30 text-xs">Lower</Badge>
                    )}
                </div>
            </div>
            <div className="relative h-6 bg-zinc-800/50 rounded-full overflow-hidden">
                <div
                    className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full transition-all duration-700"
                    style={{ width: `${country.interest}%` }}
                />
            </div>
        </div>
    );

    return (
        <>
        <div className="w-full">
                <main className="p-6 overflow-y-auto custom-scrollbar">
                    <div className="max-w-5xl mx-auto space-y-6">

                        {/* Search Bar */}
                        <div className="relative z-20">
                            <div className="bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-1 flex items-center shadow-lg shadow-black/30">
                                <div className="pl-4 pr-3">
                                    <GlobeIcon className="w-5 h-5 text-violet-400" />
                                </div>
                                <input
                                    type="text"
                                    className="flex-1 bg-transparent border-none h-11 text-sm text-white placeholder:text-zinc-500 focus:ring-0 focus:outline-none"
                                    placeholder="Enter a keyword to see geographic interest (e.g., 'football', 'AI', 'fitness')..."
                                    value={keyword}
                                    onChange={(e) => setKeyword(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleAnalyze()}
                                />
                                <button
                                    onClick={() => handleAnalyze()}
                                    disabled={loading || !keyword.trim()}
                                    className="bg-white text-zinc-950 h-9 px-6 rounded-xl font-bold text-xs hover:bg-zinc-200 transition-all shadow-md shadow-white/5 disabled:opacity-50 disabled:cursor-not-allowed ml-3 mr-1"
                                >
                                    {loading ? 'Analyzing...' : 'Analyze Geography'}
                                </button>
                            </div>
                        </div>

                        {/* Recent Pills */}
                        {recentSearches.length > 0 && (
                            <div className="flex flex-wrap gap-2 items-center">
                                <span className="text-zinc-500 text-xs">Recent:</span>
                                {recentSearches.map((item, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => handleRecentClick(item)}
                                        disabled={loading}
                                        className="px-3 py-1 rounded-full text-xs bg-zinc-800/60 border border-white/10 text-zinc-300 hover:bg-zinc-700/60 hover:text-white transition-all disabled:opacity-50"
                                    >
                                        {item.keyword}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Error */}
                        {error && (
                            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 text-sm">
                                {error}
                            </div>
                        )}

                        {/* Loading */}
                        {loading && (
                            <div className="py-40 flex flex-col items-center justify-center space-y-6">
                                <div className="relative">
                                    <div className="w-16 h-16 border-4 border-white/5 rounded-full" />
                                    <div className="absolute top-0 left-0 w-16 h-16 border-4 border-t-violet-500 rounded-full animate-spin" />
                                </div>
                                <div className="text-center space-y-2">
                                    <h3 className="text-xl font-semibold text-white">Analyzing Geographic Interest...</h3>
                                    <p className="text-zinc-500 text-sm italic">"Think globally, act locally."</p>
                                </div>
                            </div>
                        )}

                        {/* Empty State */}
                        {!geoData && !loading && (
                            <div className="space-y-6 py-12">
                                <div className="text-center space-y-3 max-w-xl mx-auto">
                                    <h1 className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-white via-zinc-200 to-zinc-400 bg-clip-text text-transparent">
                                        🌍 Geographic Content Optimizer
                                    </h1>
                                    <p className="text-zinc-400 text-sm">
                                        See where keywords are most popular and target the right audience
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="group bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-xl p-4 hover:border-white/20 transition-all hover:shadow-lg hover:shadow-violet-500/10">
                                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                            <GlobeIcon className="w-6 h-6 text-violet-400" />
                                        </div>
                                        <h3 className="text-lg font-semibold text-white mb-1.5">Global Interest Maps</h3>
                                        <p className="text-zinc-400 text-xs">See exactly where your keywords are most popular worldwide.</p>
                                    </div>

                                    <div className="group bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-xl p-4 hover:border-white/20 transition-all hover:shadow-lg hover:shadow-blue-500/10">
                                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                            <TargetIcon className="w-6 h-6 text-blue-400" />
                                        </div>
                                        <h3 className="text-lg font-semibold text-white mb-1.5">Localize Content</h3>
                                        <p className="text-zinc-400 text-xs">Create region-specific content that resonates with local audiences.</p>
                                    </div>

                                    <div className="group bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-xl p-4 hover:border-white/20 transition-all hover:shadow-lg hover:shadow-emerald-500/10">
                                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-500/20 to-green-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                            <RocketIcon className="w-6 h-6 text-emerald-400" />
                                        </div>
                                        <h3 className="text-lg font-semibold text-white mb-1.5">2x–10x More Traffic</h3>
                                        <p className="text-zinc-400 text-xs">Target high-interest regions for massive traffic gains.</p>
                                    </div>
                                </div>

                                <div className="text-center">
                                    <p className="text-zinc-500 text-xs flex items-center justify-center gap-2">
                                        <MagnifyingGlassIcon className="w-3.5 h-3.5" />
                                        Try "football", "AI", or "fitness" to see regional differences
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Results */}
                        {geoData && !loading && (
                            <div className="space-y-6">
                                {/* Header row with cache indicator + refresh */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        {geoData.is_fresh === false && (
                                            <Badge className="bg-zinc-700/50 text-zinc-400 border-zinc-600/50 text-xs gap-1">
                                                <UpdateIcon className="w-3 h-3" /> Cached
                                            </Badge>
                                        )}
                                        {geoData.is_fresh && (
                                            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">Live data</Badge>
                                        )}
                                    </div>
                                    <button
                                        onClick={handleRefresh}
                                        disabled={loading}
                                        className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-200 transition-colors disabled:opacity-50"
                                    >
                                        <ReloadIcon className="w-3.5 h-3.5" /> Refresh
                                    </button>
                                </div>

                                {/* Interest by Country */}
                                <Card className="bg-zinc-900/40 backdrop-blur-xl border-white/10">
                                    <CardHeader>
                                        <CardTitle className="text-2xl text-white">
                                            🗺️ Interest by Country: "{geoData.keyword}"
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-4">
                                            {geoData.countries.map((country, idx) => (
                                                <InterestBar key={idx} country={country} />
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Related Queries */}
                                {geoData.top_queries?.length > 0 && (
                                    <Card className="bg-zinc-900/40 backdrop-blur-xl border-white/10">
                                        <CardHeader>
                                            <CardTitle className="text-lg text-white">📊 Top Related Searches</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="space-y-2">
                                                {geoData.top_queries.map((q, idx) => (
                                                    <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                                                        <div className="flex items-center gap-2 text-sm text-zinc-300">
                                                            <div className="w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0" />
                                                            "{q.query}"
                                                        </div>
                                                        {q.value > 0 && (
                                                            <span className="text-xs text-zinc-500 tabular-nums">{q.value}</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Recommendation + Create Version */}
                                {localizationCandidates.length > 0 && (
                                    <Card className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border-violet-500/20">
                                        <CardHeader>
                                            <CardTitle className="text-lg text-white flex items-center gap-2">
                                                💡 GEOGRAPHIC OPPORTUNITIES
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            <p className="text-zinc-400 text-sm">
                                                These markets show strong interest in <span className="text-white font-medium">"{geoData.keyword}"</span> and will benefit from content written specifically for their audience — local context, culture, and search behaviour.
                                            </p>

                                            <div className="space-y-3">
                                                {localizationCandidates.map((country, idx) => (
                                                    <div key={idx} className="flex items-center gap-4 p-4 bg-zinc-800/40 rounded-xl border border-white/5">
                                                        <span className="text-3xl">{country.flag}</span>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="font-semibold text-white">{country.name}</div>
                                                            <div className="text-xs text-zinc-400 mt-0.5">
                                                                Relative interest: <span className="text-emerald-400 font-medium">{country.interest}</span>
                                                            </div>
                                                            <div className="text-xs text-violet-300 mt-1">
                                                                → Will write: <span className="italic">"{geoData.keyword} in {country.name}"</span>
                                                            </div>
                                                        </div>
                                                        <Button
                                                            size="sm"
                                                            className="bg-violet-600 hover:bg-violet-700 gap-1.5 shrink-0 text-white"
                                                            onClick={() => handleCreateVersion(country)}
                                                        >
                                                            <PlusIcon className="w-3.5 h-3.5" />
                                                            Write
                                                        </Button>
                                                    </div>
                                                ))}
                                            </div>

                                            {localizationCandidates.length >= 2 && (
                                                <Button
                                                    variant="outline"
                                                    className="w-full gap-2"
                                                    onClick={handleCreateBoth}
                                                >
                                                    <CheckCircledIcon className="w-4 h-4" />
                                                    Start with {localizationCandidates[0].name} (highest opportunity)
                                                </Button>
                                            )}
                                        </CardContent>
                                    </Card>
                                )}
                            </div>
                        )}
                    </div>
                </main>
        </div>

        {showQuickPost && (
            <QuickPost
                user={user}
                onClose={() => setShowQuickPost(false)}
                prefilledTopic={prefilledTopic}
                prefilledLocation={prefilledLocation}
            />
        )}
        </>
    );
}

export default GeographicOptimizer;
